import * as angular from 'angular';
import * as _ from 'lodash';

import { ITreeItem, ComponentDefinition, Pattern } from '../../models';
import { IUserSettingsService, ICurrentApplicationService } from '../../services/';
import { guidEmpty } from '../../common/constants';
import { treeViewNavigationStart, treeViewNavigationEnd } from '../../common/events';

interface TreeItem extends ITreeItem {
    isCollapsed: boolean;
}

class TreeViewController implements angular.IComponentController {
    filter: string;
    filteredItems: TreeItem[];
    isDefaultCollapse: boolean;
    isLoading: boolean;
    isMobileMode: boolean;
    items: TreeItem[];
    onLinkClick: () => void;
    state: angular.ui.IState;
    url: any;
    urlParams: any;

    readonly searchCriteria: Pattern<any>[];

    private _filterWatcher: () => void;
    private _garbages: (() => void)[] = [];
    private _padding = 20;
    private _settings = '-tree-state-';
    private defaultMaxDepth: number = 2;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        'userSettingsService',
        'currentApplicationService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$state: angular.ui.IStateService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _userSettingsService: IUserSettingsService,
        private _currentApplicationService: ICurrentApplicationService) {
    }

    $onInit() {
        this.isLoading = true;
        this.items = [];
        this.filteredItems = [];

        // When tree items change, update tree view
        this._$scope.$watch(() => this.items, (newValue: any[]) => {
            if (newValue) {
                newValue.forEach(d => {
                    d.style = this.getStyle(d);
                    d.url = this.getUrl(d);
                    if (!d.uniqueId) {
                        d.uniqueId = d.name;
                    }
                });
                this.filteredItems = this.items;
                if (this._filterWatcher) {
                    this._filterWatcher();
                }
                if (!this.searchCriteria) {
                    this.watchFilter();
                }
                this.isLoading = false;
            }
        });

        // On state change, setup items url
        this._$scope.$watchGroup([() => this._$state.current, () => this.url], () => {
            if (this.url) {
                const stateName = this._$state.current.name;
                const firstPart = stateName.split('.');
                if (firstPart[0] === this.url.split('.')[0]) {
                    this.items.forEach((i: any) => {
                        i.url = this.getUrl(i, stateName);
                    });
                }
            }
        });

        this._garbages.push(
            this._$rootScope.$on(treeViewNavigationStart, () => this.isLoading = true),
            this._$rootScope.$on(treeViewNavigationEnd, () => this.isLoading = false)
        );
    }

    private watchFilter() {
        this._filterWatcher = this._$scope.$watch(() => this.filter, (nv) => {
            if (!nv) {
                nv = '';
            }
            const searchString = nv.toLowerCase();
            this.filteredItems = this.filterItems(this.items, searchString);
            //self.filteredItems.forEach(i => self.expandTree(i));
            // todo load according other criteria
            this.loadTreeState();
        });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['searchCriteria']) {
            if (this._filterWatcher) {
                this._filterWatcher();
            }
            if (!onChangesObj['searchCriteria'].currentValue) {
                this.watchFilter();
            }
        }
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    /**
     * Check if a tree item is active
     * @param item Target tree item
     */
    isActive(item: TreeItem): boolean {
        let result = false;
        if (this._$state.current.name === this.url) {
            for (const key of Object.keys(this.urlParams)) {
                if (this._$stateParams[key] && this.urlParams[key](item) === this._$stateParams[key]) {
                    result = true;
                }
            }
        }
        return result;
    }

    /**
     * Handle on click on arrow to expand / collapse children items
     * @param item Target item
     * @param $event Event
     */
    toggleChildren(item: TreeItem, $event: angular.IAngularEvent): void {
        if (item.children) {
            this.collapseItem(item);
        } else {
            this.expandItem(item);
        }
        this.saveTreeState();
        $event.preventDefault();
        $event.stopPropagation();
    }

    /**
     * Change collapsed/expanded state
     */
    switchCollapse() {
        const ret = this.isDefaultCollapse
            ? this.expandAll()
            : this.collapseAll();

        return ret;
    }

    /**
     * Expand all nodes
     */
    private expandAll(): void {
        this.filteredItems.forEach((item) => {
            if (item.depth > 0) {
                this.expandItem(item);
            }
        });
    }

    /**
     * Collapse all nodes
     */
    private collapseAll(): void {
        this.filteredItems.forEach(item => {
            if (item.depth > this.defaultMaxDepth) {
                this.collapseItem(item);
            }
        });
        this.isDefaultCollapse = true;
    }

    /**
     * Get state setting name
     * TODO : Use REDUX to recover the name
     */
    private getStateSettingName(): string {
        return `${this._currentApplicationService.getApplicationConfiguration().name}${this._settings}${this.state.name}`;
    }

    /**
     * Save current tree state
     */
    private saveTreeState() {
        const treeState = {};
        this.items.forEach(i => treeState[i.guid] = i.isCollapsed);
        this._userSettingsService.setSettingValue(this.getStateSettingName(), JSON.stringify(treeState));
    }

    /**
     * Display tree in last known state
     */
    private loadTreeState() {
        if (this.state) {
            this.defaultMaxDepth = (this.state.name === 'models.structure') ? 2 : 0;

            let treeState: { [guid: string]: boolean };
            try {
                treeState = JSON.parse(
                    this._userSettingsService.getSettingValue(this.getStateSettingName())
                );
            } catch (e) { /*empty*/ }

            if (treeState) {
                // force empty guid (root) to be expanded
                treeState[guidEmpty] = false;

                this.filteredItems.forEach(i => {
                    if (treeState[i.guid]) {
                        this.collapseItem(i);
                    } else {
                        this.expandItem(i);
                    }
                });
            } else {
                this.defaultRendering();
            }
        }
    }

    /**
     * Default rendering of the tree
     * This will expand only 2 level and above
     */
    private defaultRendering() {
        this.filteredItems.forEach(i => {
            if (i.depth <= this.defaultMaxDepth) {
                this.expandItem(i);
            } else {
                this.collapseItem(i);
            }
        });
        this.isDefaultCollapse = true;
    }

    /**
     * Filter tree items based on searchbox
     * @param items Tree items list
     * @param searchString Filter string
     */
    private filterItems(items: any[], searchString: string): any[] {
        items.filter(i => i.itemType !== 'Category' && i.name && i.name.toLowerCase().indexOf(searchString) < 0)
            .forEach(i => i.isVisible = false);
        const filteredItems = items.filter(i => i.itemType !== 'Category' && i.name && i.name.toLowerCase().indexOf(searchString) > -1);
        filteredItems.forEach(fi => {
            let current: any = fi;
            current.isVisible = true;
            while (current.parent && current.guid !== guidEmpty) {
                const found = _.find(items, i => i.guid === current.parent.guid);
                found.isVisible = true;
                current = found;
            }
        });
        return items;
    }

    /**
     * Get tree item url
     * @param item Tree item to get url for
     * @param url Default url
     */
    private getUrl(item: TreeItem, url?: string): string {
        // If url provided is empty, use default url
        if (!url) {
            url = this.url;
        }
        // If item url is set and different from provided url, use item url.
        // Item url override provided url, it's useful for dashboards and categories
        if (item.url && url !== item.url && item.itemType === 'Category') {
            url = 'dashboards';
        }
        const params: any = {};
        for (const key of Object.keys(this.urlParams)) {
            params[key] = this.urlParams[key](item);
        }
        const href = this._$state.href(url, params);
        return href;
    }

    /**
     * Set tree item visibility (For collapsing/expading)
     * @param item Target tree item
     * @param isVisible Visibility of the tree item
     */
    private changeVisibility(item: TreeItem, isVisible: boolean) {
        this.changeVisibilityOnCollection(item.children, isVisible);
        this.changeVisibilityOnCollection(item._children, isVisible);
    }

    private changeVisibilityOnCollection(list: ITreeItem<any>[], isVisible: boolean) {
        list?.forEach(c => {
            c.isVisible = isVisible;
            if (!isVisible) {
                this.changeVisibility(<any>c, isVisible);
            } else {
                this.collapseItem(<any>c);
            }
        });
    }

    /**
     * Handle indentation for displaying elements in tree
     * @param item Tree item to get the style for
     */
    private getStyle(item: TreeItem): any {
        let style = {};
        if (item.depth !== 0) {
            style = {
                'padding-left': ((item.depth - 1) * this._padding) + 'px'
            };
        }
        return style;
    }

    /**
     * Expand a specific node
     * @param item Tree item to expand
     */
    private expandTree(item: TreeItem) {
        this.expandItem(item);
        if (item.children) {
            item.children.forEach(c => this.expandTree(<any>c));
        }
    }

    /**
     * Collapse a specific node
     * @param item Tree item to collapse
     */
    private collapseTree(item: TreeItem) {
        if (item.children) {
            item.children.forEach(c => this.collapseTree(<any>c));
        }
        this.collapseItem(item);
    }

    /**
     * Expand a tree item
     * @param item Tree item to expand
     */
    private expandItem(item: TreeItem) {
        if (item._children) {
            item.children = item._children;
            this.changeVisibility(item, true);
            item._children = null;
            item.isCollapsed = false;
        }
        this.isDefaultCollapse = false;
    }

    /**
     * Collapse a tree item
     * @param item Tree item to collapse
     */
    private collapseItem(item: TreeItem) {
        if (item.children) {
            this.changeVisibility(item, false);
            item._children = item.children;
            item.children = null;
            item.isCollapsed = true;
        }
        this.isDefaultCollapse = false;
    }

    onItemClick($event: angular.IAngularEvent) {
        if (!$event.defaultPrevented) {
            if (this.onLinkClick) {
                this.onLinkClick();
            }
        }
    }
}

export const wtTreeView: ComponentDefinition = {
    componentId: 'wtTreeView',
    controller: TreeViewController,
    template: require('./treeView.component.html'),
    controllerAs: 'vm',
    bindings: {
        items: '=',
        onLinkClick: '&?',
        searchCriteria: '<?',
        searchPlaceholder: '<?',
        state: '=',
        url: '=',
        urlParams: '=',
    }
};
